import {environment} from 'src/environments/environment';

const IMAGE_FORMAT = environment.noWebp ? 'image/jpeg' : 'image/webp';

export function compressFile(input: File | string, maxSize: number, options?: {isBytes?: boolean}): Promise<string> {
  const isBytes = options?.isBytes ?? false;
  const targetBytes = isBytes ? maxSize : Math.round(maxSize * 1024 * 1024);

  const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target!.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const getBase64Size = (b64: string): number => {
    const padding = (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
    return (b64.length * 3 / 4) - padding;
  };

  return new Promise<string>((resolve, reject) => {
    (async () => {
      try {
        let originalB64: string;
        if(typeof input === 'string') {
          originalB64 = input;
        } else {
          originalB64 = await fileToBase64(input);
        }

        const originalSize = getBase64Size(originalB64);
        if(originalSize <= targetBytes) {
          resolve(originalB64);
          return;
        }

        // Only images can be compressed via canvas
        if(!originalB64.startsWith('data:image/')) {
          resolve(originalB64);
          return;
        }

        const img = await new Promise<HTMLImageElement>((res, rej) => {
          const i = new Image();
          i.onload = () => res(i);
          i.onerror = rej;
          i.src = originalB64;
        });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        let width = img.width;
        let height = img.height;
        let quality = 0.9;

        while(width > 150 && height > 150) {
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          const b64 = canvas.toDataURL(IMAGE_FORMAT, quality);
          const size = getBase64Size(b64);

          if(size <= targetBytes) {
            resolve(b64);
            return;
          }

          quality -= 0.1;
          if(quality < 0.05) {
            quality = 0.9;
            width = Math.floor(width * 0.85);
            height = Math.floor(height * 0.85);
          }
        }

        reject('Unable to compress the image sufficiently.');
      } catch (err) {
        reject(err);
      }
    })();
  });
}

export async function compressImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target!.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const mimeType = file.type;

        canvas.width = img.width;
        canvas.height = img.height;
        ctx!.drawImage(img, 0, 0, img.width, img.height);

        const startQuality = 0.6;
        const minQuality = 0.15;
        const targetRatio = 0.10;
        const step = 0.05;

        let quality = startQuality;
        let compressedFile: File;
        let compressedSizeKB = file.size / 1024;
        const originalSizeKB = compressedSizeKB;

        do {
          const dataUrl = canvas.toDataURL(mimeType, quality);
          const byteString = atob(dataUrl.split(',')[1]);
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const intArray = new Uint8Array(arrayBuffer);
          for(let i = 0; i < byteString.length; i++) intArray[i] = byteString.charCodeAt(i);

          compressedFile = new File([intArray], file.name, {type: mimeType});
          compressedSizeKB = compressedFile.size / 1024;

          quality -= step;
        } while(compressedSizeKB > originalSizeKB * targetRatio && quality >= minQuality);

        resolve(compressedFile);
      };

      img.onerror = reject;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
